import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Check, ChevronRight, Lock, Upload, Plug, Globe } from 'lucide-react';
import { PLATFORM_NAV_ITEMS } from '../config/platformNav';
import useEntitlements, { NAV_MODULE_MAP, OPEN_NAV_IDS } from '../hooks/useEntitlements';
import {
  GETTING_STARTED_GUIDES,
  GETTING_STARTED_INTRO,
  GETTING_STARTED_STEPS,
  GETTING_STARTED_TILES,
  GETTING_STARTED_VIDEO,
  EXPLORE_PILLARS,
} from '../data/gettingStartedConfig';
import { GUIDE_STORAGE_KEY } from '../data/platformTutorial';
import { deriveGettingStartedProgress } from '../lib/gettingStartedProgress';

const TILE_ICONS = {
  'import-contacts': Upload,
  'connect-sources': Plug,
  'set-domain': Globe,
};

function isNavAllowed(navId, entitlements, canAny) {
  if (!navId || OPEN_NAV_IDS.has(navId)) return true;
  if (!entitlements) return true;
  const mods = NAV_MODULE_MAP[navId];
  if (!mods) return true;
  return canAny(mods);
}

export default function GettingStartedPanel({ dashboardData }) {
  const navigate = useNavigate();
  const { canAny, entitlements } = useEntitlements();
  const progress = useMemo(
    () => deriveGettingStartedProgress(dashboardData),
    [dashboardData],
  );
  const [activeStep, setActiveStep] = useState(progress.activeStepIndex);
  const [tourSeen] = useState(() => Boolean(localStorage.getItem(GUIDE_STORAGE_KEY)));

  // Keep the active (gold) step synced to the first incomplete when data loads/refreshes.
  // User can still click another step; we only auto-jump when the derived active index changes.
  useEffect(() => {
    setActiveStep(progress.activeStepIndex);
  }, [progress.activeStepIndex]);

  const navById = useMemo(() => {
    const map = {};
    PLATFORM_NAV_ITEMS.forEach((item) => { map[item.id] = item; });
    return map;
  }, []);

  const go = (path, navId) => {
    const allowed = isNavAllowed(navId, entitlements, canAny);
    navigate(allowed ? path : '/subscription-plans');
  };

  const videoHref = (GETTING_STARTED_VIDEO.href || '').trim();

  return (
    <section
      className="rounded-2xl border border-[#fbbf0f]/20 bg-[#11100c]/80 overflow-hidden"
      data-testid="getting-started-panel"
      data-completed={progress.completedCount}
      data-total={progress.total}
    >
      {/* 1. Header */}
      <div className="px-5 pt-5 pb-3">
        <h2 className="text-lg font-semibold text-[#faf7f2] tracking-tight" data-testid="getting-started-title">
          Getting started
        </h2>
        <p className="text-[11px] text-[#8a7f6e] mt-0.5" data-testid="getting-started-progress-label">
          {progress.completedCount} of {progress.total} complete
          {progress.allComplete ? ' — nice work' : ''}
        </p>
      </div>

      {/* 2. Resources strip — compact video left, intro + guides right */}
      <div className="px-5 pb-5 border-b border-white/[0.06]">
        <div className="flex flex-col sm:flex-row gap-5 sm:gap-6 sm:items-start">
          {/* Video — left, compact 16:9 */}
          <div
            className="w-full sm:w-[min(100%,320px)] sm:max-w-[320px] sm:shrink-0"
            data-testid="getting-started-video"
          >
            <div className="rounded-xl border border-[#fbbf0f]/30 bg-[#0a0908] overflow-hidden shadow-[0_0_0_1px_rgba(251,191,15,0.08)]">
              {videoHref ? (
                <video
                  controls
                  playsInline
                  preload="metadata"
                  className="block w-full aspect-video bg-black"
                  src={videoHref}
                >
                  Your browser does not support embedded video.
                </video>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate(GETTING_STARTED_VIDEO.fallbackPath || '/platform-guide?section=welcome')}
                  className="w-full aspect-video flex items-center justify-center px-4 text-sm text-[#c4b8a5] hover:text-[#fbbf0f] transition-colors"
                >
                  Open Platform Guide for an overview
                </button>
              )}
            </div>
            <p className="mt-2 text-center sm:text-left text-xs font-medium text-[#c4b8a5]">
              {GETTING_STARTED_VIDEO.label}
            </p>
          </div>

          {/* Intro + guides — right */}
          <div className="min-w-0 flex-1">
            <p className="text-sm text-[#c4b8a5] mb-3 max-w-2xl">{GETTING_STARTED_INTRO}</p>
            {tourSeen ? (
              <p className="mb-3 text-[11px] text-[#6b6256]" data-testid="getting-started-tour-note">
                Welcome tour already seen — this checklist tracks live account progress.
              </p>
            ) : null}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {GETTING_STARTED_GUIDES.map((guide) => (
                <button
                  key={guide.id}
                  type="button"
                  onClick={() => navigate(guide.path)}
                  className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left hover:border-[#fbbf0f]/35 hover:bg-[#fbbf0f]/5 transition-colors"
                  data-testid={`getting-started-guide-${guide.id}`}
                >
                  <BookOpen className="h-4 w-4 shrink-0 text-[#fbbf0f]" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-[#faf7f2]">{guide.label}</span>
                    <span className="block text-[11px] text-[#8a7f6e] mt-0.5">Platform Guide</span>
                  </span>
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[#6b6256]" />
                </button>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-[#6b6256]">
              Need a hand?{' '}
              <a
                href="mailto:support@snagcart.io?subject=Snagcart%20setup%20help"
                className="text-[#c4b8a5] hover:text-[#fbbf0f] underline underline-offset-2"
                data-testid="getting-started-support-link"
              >
                Email support@snagcart.io
              </a>
              {' · '}
              <button
                type="button"
                onClick={() => navigate('/support')}
                className="text-[#c4b8a5] hover:text-[#fbbf0f] underline underline-offset-2"
              >
                Support
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* 3–4. Subhead + two columns */}
      <div className="px-5 py-5 border-b border-white/[0.06]">
        <h3 className="text-sm font-semibold text-[#faf7f2] mb-4" data-testid="getting-started-subhead">
          Getting started with Snagcart
        </h3>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-8 lg:gap-10">
          {/* LEFT — numbered stepper */}
          <ol className="space-y-0" data-testid="getting-started-stepper">
            {GETTING_STARTED_STEPS.map((step, index) => {
              const done = Boolean(progress.completed[step.id]);
              const isActive = index === activeStep;
              return (
                <li
                  key={step.id}
                  className={`border-b border-white/[0.06] last:border-b-0 ${
                    isActive && !done ? 'bg-[#fbbf0f]/5 -mx-2 px-2 rounded-lg' : ''
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setActiveStep(index)}
                    className="w-full flex items-start gap-3 py-3.5 text-left group"
                    data-testid={`getting-started-step-${step.id}`}
                    data-completed={done ? 'true' : 'false'}
                    data-active={isActive ? 'true' : 'false'}
                    aria-current={isActive ? 'step' : undefined}
                  >
                    <span
                      className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                        done
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                          : isActive
                            ? 'bg-[#fbbf0f] text-[#16130d] border-2 border-[#16130d]'
                            : 'border border-white/20 text-[#8a7f6e] group-hover:border-[#fbbf0f]/40 group-hover:text-[#c4b8a5]'
                      }`}
                    >
                      {done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : index + 1}
                    </span>
                    <span className="min-w-0 flex-1 pt-0.5">
                      <span
                        className={`block text-sm font-semibold ${
                          done
                            ? 'text-[#8a7f6e] line-through decoration-[#6b6256]/80'
                            : isActive
                              ? 'text-[#faf7f2]'
                              : 'text-[#a89882] group-hover:text-[#d9cebc]'
                        }`}
                      >
                        {step.title}
                      </span>
                      {isActive ? (
                        <>
                          <span className="block text-xs text-[#8a7f6e] mt-1 leading-relaxed">{step.blurb}</span>
                          {!done ? (
                            <span className="mt-2.5 flex flex-wrap gap-2">
                              {step.actions.map((action, ai) => {
                                const allowed = isNavAllowed(action.navId, entitlements, canAny);
                                const emphasize = ai === 0;
                                return (
                                  <button
                                    key={`${step.id}-${action.path}`}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      go(action.path, action.navId);
                                    }}
                                    className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${
                                      emphasize
                                        ? 'bg-[#fbbf0f] text-[#16130d] border border-[#16130d] hover:bg-[#ffd84a] shadow-[0_0_0_1px_rgba(251,191,15,0.35)]'
                                        : 'border border-white/15 text-[#d9cebc] hover:border-[#fbbf0f]/40 hover:text-white'
                                    }`}
                                    data-testid={`getting-started-cta-${step.id}-${ai}`}
                                    title={allowed ? action.label : 'Upgrade plan to unlock'}
                                  >
                                    {emphasize ? (
                                      <span className="opacity-90">Continue</span>
                                    ) : null}
                                    {emphasize ? <span className="opacity-50">·</span> : null}
                                    {action.label}
                                    {!allowed ? <Lock className="h-3 w-3" /> : null}
                                  </button>
                                );
                              })}
                            </span>
                          ) : (
                            <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-400/90">
                              <Check className="h-3 w-3" />
                              Done
                            </span>
                          )}
                        </>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>

          {/* RIGHT — setup tiles */}
          <div className="space-y-3" data-testid="getting-started-tiles">
            {GETTING_STARTED_TILES.map((tile) => {
              const Icon = TILE_ICONS[tile.id] || Plug;
              const allowed = isNavAllowed(tile.navId, entitlements, canAny);
              return (
                <button
                  key={tile.id}
                  type="button"
                  onClick={() => go(tile.path, tile.navId)}
                  className="w-full rounded-xl border border-white/12 bg-transparent px-4 py-3.5 text-left hover:border-[#fbbf0f]/40 hover:bg-[#fbbf0f]/5 transition-colors"
                  data-testid={`getting-started-tile-${tile.id}`}
                  title={allowed ? tile.cta : 'Upgrade plan to unlock'}
                >
                  <div className="flex items-start gap-3">
                    <Icon className="h-4 w-4 shrink-0 mt-0.5 text-[#fbbf0f]" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-[#faf7f2]">{tile.title}</p>
                        {!allowed ? <Lock className="h-3 w-3 text-[#6b6256]" /> : null}
                      </div>
                      <p className="text-[11px] text-[#8a7f6e] mt-1 leading-relaxed">{tile.blurb}</p>
                      <p className="mt-2 text-[11px] font-semibold text-[#fbbf0f] inline-flex items-center gap-0.5">
                        {tile.cta}
                        <ChevronRight className="h-3 w-3" />
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* 5. Explore modules */}
      <div className="px-5 py-5" data-testid="getting-started-explore">
        <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8a7f6e] mb-3">
          Explore modules
        </h3>
        <div className="space-y-3">
          {EXPLORE_PILLARS.map((pillar) => (
            <div
              key={pillar.id}
              className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4"
              data-testid={`explore-pillar-${pillar.id}`}
            >
              <span className="w-16 shrink-0 pt-1 text-[11px] font-bold uppercase tracking-wider text-[#fbbf0f]/80">
                {pillar.label}
              </span>
              <div className="flex flex-wrap gap-1.5 min-w-0">
                {pillar.navIds.map((navId) => {
                  const item = navById[navId];
                  if (!item) return null;
                  const allowed = isNavAllowed(navId, entitlements, canAny);
                  return (
                    <button
                      key={navId}
                      type="button"
                      onClick={() => go(item.path, navId)}
                      className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors ${
                        allowed
                          ? 'border-white/10 text-[#d9cebc] hover:border-[#fbbf0f]/40 hover:text-[#faf7f2] hover:bg-[#fbbf0f]/8'
                          : 'border-white/[0.06] text-[#6b6256] hover:border-white/15 hover:text-[#a89882]'
                      }`}
                      data-testid={`explore-chip-${navId}`}
                      title={allowed ? item.label : 'Upgrade plan to unlock'}
                    >
                      {item.label}
                      {!allowed ? <Lock className="h-2.5 w-2.5" /> : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
